export type PipelineStageId =
  | 'plant-data'
  | 'signals'
  | 'excursions'
  | 'cases'
  | 'diagnosis'
  | 'action'
  | 'value'
  | 'learn';

export type PipelineActor = 'System' | 'Engine' | 'Decision model' | 'Agent' | 'Human';

export type PipelineStage = {
  id: PipelineStageId;
  label: string;
  actors: readonly PipelineActor[];
};

export type PipelineCellRead = {
  count: string;
  countContext?: string;
  comparison: string;
  /** A lead term set in 600 before `comparison` (`20%` then `of 10 in the
   *  register`). Without it the comparison splits at its first ` · `. */
  comparisonLead?: string;
  money?: string;
  moneyComparison?: string;
  /**
   * K15: an actor chip's own read, replacing the actor word on that chip only.
   * The Diagnosis stage counts its investigation runs on the Agent chip.
   */
  actorCounts?: Partial<Record<PipelineActor, string>>;
};

export const PIPELINE_STAGES: readonly PipelineStage[] = [
  { id: 'plant-data', label: 'Plant data', actors: ['System'] },
  { id: 'signals', label: 'Signals', actors: ['Engine'] },
  { id: 'excursions', label: 'Excursions', actors: ['Engine'] },
  { id: 'cases', label: 'Cases', actors: ['Decision model'] },
  { id: 'diagnosis', label: 'Diagnosis', actors: ['Engine', 'Agent'] },
  { id: 'action', label: 'Action', actors: ['Human'] },
  { id: 'value', label: 'Value', actors: ['Engine'] },
  { id: 'learn', label: 'Learn', actors: ['Human'] },
];

const LEGACY_STAGE: Readonly<Record<string, PipelineStageId>> = {
  queue: 'cases',
  'driver-tree': 'value',
  approvals: 'learn',
  alarms: 'signals',
  'rule-set': 'learn',
};

const STAGE_IDS = new Set(PIPELINE_STAGES.map((stage) => stage.id));

/** Resolve both current stage slugs and the former Advisory path/query values. */
export function stageForAdvisoryParam(value: string | null | undefined): PipelineStageId | null {
  if (!value) return null;
  if (STAGE_IDS.has(value as PipelineStageId)) return value as PipelineStageId;
  return LEGACY_STAGE[value] ?? null;
}

export function stageForAdvisoryLocation(input: {
  pathTab?: string | null;
  queryTab?: string | null;
  hasReview?: boolean;
  hasCause?: boolean;
}): PipelineStageId {
  return (
    (input.hasReview ? 'learn' : null) ??
    stageForAdvisoryParam(input.pathTab) ??
    stageForAdvisoryParam(input.queryTab) ??
    (input.hasCause ? 'learn' : null) ??
    'cases'
  );
}

export function pipelineStageIndex(stage: PipelineStageId): number {
  return PIPELINE_STAGES.findIndex((row) => row.id === stage);
}
